import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { groupBy } from 'lodash';
import { Alert } from '@mui/material';
import { QueryKey } from '../../enums/QueryKey';
import { useDispatchErrorNotification, useDispatchSuccessNotification, useWorkingUnitLocations } from '../../hooks';
import { queryClient } from '../../services/queryClient';
import { releaseCorrectedTotals } from '../../services/correctedTotals';
import { useCorrectedTotalsForLocation } from '../../hooks/api/correctedTotals';
import { appTimezone } from '../../config';
import ReleaseTotalsDialog from './ReleaseTotalsDialog';
import dayjs, { Dayjs } from '@utils/dayjs';

interface LocationOption {
    id: number;
    [k: string]: any;
}

const ReleaseCorrectedTotalsDialog = ({
    open,
    onClose,
    date,
    workingUnitLocation,
    selectedLocation,
}: {
    open: boolean;
    onClose: () => void;
    date: Dayjs;
    workingUnitLocation?: number;
    selectedLocation?: LocationOption;
}) => {
    const { t } = useTranslation('bidding');
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    const [selectedLocations, setSelectedLocations] = useState<LocationOption | undefined>(selectedLocation);

    const [workingUnitLocations] = useWorkingUnitLocations({
        location: selectedLocations?.id,
        hasMatchingOfferedCluster: true,
    });
    const [selectedWorkingUnits, setSelectedWorkingUnits] = useState<
        {
            workingUnitLocationId: number;
            [k: string]: any;
        }[]
    >([]);
    const [correctedTotals] = useCorrectedTotalsForLocation({ date, location: selectedLocations?.id });
    const correctedTotalsByWorkingUnit = groupBy(correctedTotals, 'workingUnitLocationId');
    const workingUnitOptions = workingUnitLocations.map((workingUnitLocation) => {
        const totals = correctedTotalsByWorkingUnit[workingUnitLocation.id];
        const unreleasedTotal = totals?.find((total) => !total.released);
        return {
            workingUnitLocationId: workingUnitLocation.id,
            ...workingUnitLocation.workingUnit,
            disabled: totals?.length > 0 && !unreleasedTotal,
        };
    });

    const { mutateAsync: handleReleaseCorrectedTotals, isLoading } = useMutation({
        mutationFn: releaseCorrectedTotals,
        onSuccess: (res) => {
            if (res === 0) {
                return dispatchErrorNotification({ message: t('release.noData') });
            }

            dispatchSuccessNotification({ message: t('notification.dataSaved') });
            onClose();
        },
        onError: (error: Error) => {
            dispatchErrorNotification({ message: error.message || t('error.dataNotSaved') });
        },
        onSettled: () => {
            queryClient.invalidateQueries([QueryKey.LastCorrectedTotalsReleaseDate, { workingUnitLocation }]);
            // Releasing changes the selector's "released through {month}" caption, which derives from
            // corrected totals via the async metadata query — refresh it so it doesn't stay stale.
            queryClient.invalidateQueries({ queryKey: [QueryKey.WorkingUnitLocationMetadata] });
        },
    });

    // @ts-ignore
    return (
        <ReleaseTotalsDialog
            open={open}
            onClose={onClose}
            date={date}
            onRelease={() =>
                handleReleaseCorrectedTotals({
                    dateFrom: dayjs(date).tz(appTimezone).startOf('month').toISOString(),
                    dateTo: dayjs(date).tz(appTimezone).endOf('month').toISOString(),
                    workingUnitLocation: selectedWorkingUnits.map((wu) => wu.workingUnitLocationId),
                })
            }
            selectedLocations={selectedLocations}
            onSelectedLocationsChange={setSelectedLocations}
            workingUnitOptions={workingUnitOptions}
            selectedWorkingUnits={selectedWorkingUnits}
            onSelectedWorkingUnitsChange={setSelectedWorkingUnits}
            isLocationMultiSelect={false}
            releaseButtonText={t('action.send')}
            isLoading={isLoading}
        >
            {/* @ts-ignore */}
            <Alert severity="info" sx={{ mt: 2 }} color="primary">
                {t('release.correctedTotalsHint')}
            </Alert>
        </ReleaseTotalsDialog>
    );
};

export default ReleaseCorrectedTotalsDialog;
